import { useTranslation } from 'react-i18next';
import { useCenterServices } from '@hooks/usePublicBooking';
import { PageLoading } from '@components/common/LoadingSpinner';
import { ErrorMessage } from '@components/common/ErrorMessage';
import type { Service } from '@api/types';
import { cn } from '@lib/utils';
import { formatMoney } from '@lib/money';

interface ServiceStepProps {
  centerSlug: string;
  /** ISO 4217 code from the centre; used to format service.price (minor units). */
  currency: string;
  onSelect: (service: Service) => void;
}

export function ServiceStep({ centerSlug, currency, onSelect }: ServiceStepProps) {
  const { t } = useTranslation();
  const { data: services, isLoading, isError, refetch } = useCenterServices(centerSlug);

  if (isLoading) return <PageLoading />;
  if (isError) return <ErrorMessage onRetry={() => void refetch()} />;

  const active = services?.filter((s) => s.active) ?? [];

  return (
    <section aria-label={t('widget.selectService')}>
      <h2 className="mb-4 text-lg font-semibold">{t('widget.selectService')}</h2>
      {active.length === 0 && (
        <div className="rounded-md border border-dashed bg-muted/30 p-6 text-center text-sm text-muted-foreground">
          {t('widget.noServicesAvailable', 'Este centro no tiene servicios disponibles ahora mismo.')}
        </div>
      )}
      <ul className="grid gap-3" role="list">
        {active.map((service) => {
          const priceLabel = formatMoney(service.price, currency);
          return (
            <li key={service.id}>
              <button
                type="button"
                className={cn(
                  'w-full rounded-md border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-primary/5',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
                onClick={() => { onSelect(service); }}
                data-testid={`service-option-${service.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{service.name}</div>
                    {service.description && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {service.description}
                      </div>
                    )}
                    <div className="mt-2 text-xs text-muted-foreground">
                      {service.durationMinutes} min
                    </div>
                  </div>
                  {priceLabel && (
                    <div className="shrink-0 text-sm font-semibold tabular-nums">
                      {priceLabel}
                    </div>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
